/**
 * @file Section de collecte des allergies alimentaires du parcours de réservation.
 * @module packages.common.components.table-reservation.components.AllergySection
 *
 * Design compact : radio Oui/Non, puis champ de détails affiché seulement sur
 * « Oui ». « Non » est présélectionné, le seul cas bloquant est donc « Oui »
 * sans détail.
 *
 * Le composant reste purement présentationnel. La règle vit dans
 * `packages/common/utils/restaurant/allergyDetailsRule`, partagée avec la page
 * publique de gestion de réservation ; l'état d'erreur et le texte d'aide sont
 * fournis par l'étape parente, seule à connaître le verrou du parcours.
 */

import React, {useRef, useImperativeHandle} from 'react';
import PropTypes from 'prop-types';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Fade from '@mui/material/Fade';
import InputAdornment from '@mui/material/InputAdornment';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormLabel from '@mui/material/FormLabel';

// icons
import Iconify from 'packages/common/components/iconify';
import {clientInputStyles} from '../styles/ReservationFlowStyles';

// ----------------------------------------------------------------------

// Champ texte pour les détails des allergies
const AllergyDetailsInput = styled(OutlinedInput)({
  cursor: 'text',
  width: '100%',
  ...clientInputStyles,
});

// ----------------------------------------------------------------------

/**
 * Section de collecte des allergies alimentaires
 *
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.formData - Données du formulaire
 * @param {boolean} props.formData.hasAllergies - Indique si le client a des allergies
 * @param {string} props.formData.allergyDetails - Détails des allergies
 * @param {Function} props.updateFormData - Fonction pour mettre à jour les données du formulaire
 * @param {Function} props.handleInputClick - Gestionnaire pour empêcher la propagation des clics
 * @param {Function} props.t - Fonction de traduction
 * @param {boolean} [props.error] - Affiche le champ de détails en erreur
 * @param {React.ReactNode} [props.helperText] - Texte d'aide ou message d'erreur sous le champ
 * @param {Function} [props.onDetailsBlur] - Appelé quand le client quitte le champ de détails
 * @return {JSX.Element} Section de collecte des allergies
 */
export const AllergySection = React.forwardRef(function AllergySection({
  formData,
  updateFormData,
  handleInputClick,
  t,
  error,
  helperText,
  onDetailsBlur,
}, ref) {
  // Ref pour le premier radio button
  const firstRadioRef = useRef(null);

  // Exposer une méthode focus pour permettre au parent de focuser le premier élément
  useImperativeHandle(ref, () => ({
    focus: () => {
      firstRadioRef.current?.focus();
    },
  }), []);
  /**
   * Gère le changement de valeur du switch Oui/Non
   *
   * @param {Object} event - Événement de changement du radio button
   */
  const handleAllergyChange = (event) => {
    event.stopPropagation();
    const hasAllergies = event.target.value === 'yes';
    updateFormData('hasAllergies', hasAllergies);
    // Si on sélectionne "non", on vide les détails d'allergies
    if (!hasAllergies) {
      updateFormData('allergyDetails', '');
    }
  };

  /**
   * Gère le changement du champ de détails des allergies
   *
   * @param {Object} event - Événement de changement du champ texte
   */
  const handleDetailsChange = (event) => {
    event.stopPropagation();
    updateFormData('allergyDetails', event.target.value);
  };

  /**
   * Signale au parent que le client a quitté le champ sans le remplir.
   *
   * @param {Object} event - Événement de perte de focus
   */
  const handleDetailsBlur = (event) => {
    event.stopPropagation();
    onDetailsBlur?.();
  };

  return (
    <Box sx={{mb: 3}}>
      <Box
        sx={{
          p: 2,
          borderRadius: 1,
          border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5}}>
          <Iconify icon="mdi:gluten" sx={{fontSize: 20, color: 'primary.main'}} aria-hidden="true" />
          <FormLabel component="legend" id="allergy-question-label" sx={{typography: 'body2', fontWeight: 600, color: 'text.primary'}}>
            {t ? t('widgets.reservation.form.allergy.question') : 'Y a-t-il des allergies ou intolérances alimentaires à signaler ?'}
          </FormLabel>
        </Box>

        <RadioGroup
          value={formData.hasAllergies ? 'yes' : 'no'}
          onChange={handleAllergyChange}
          aria-labelledby="allergy-question-label"
          sx={{
            flexDirection: 'row',
            gap: 2,
            mb: formData.hasAllergies ? 2 : 0,
          }}
        >
          <FormControlLabel
            value="no"
            control={<Radio size="small" inputRef={firstRadioRef} />}
            label={t ? t('widgets.reservation.form.allergy.no') : 'Non'}
          />
          <FormControlLabel
            value="yes"
            control={<Radio size="small" />}
            label={t ? t('widgets.reservation.form.allergy.yes') : 'Oui'}
          />
        </RadioGroup>

        {/* Champ de détail des allergies (affiché seulement si "Oui" est sélectionné) */}
        {formData.hasAllergies && (
          <Fade in timeout={300}>
            <Box sx={{mt: 1}} onClick={(e) => e.stopPropagation()}>
              {/* `required` et `error` sur le FormControl : MUI propage l'astérisque,
                  le contour rouge et `aria-invalid` à l'input par son contexte. */}
              <FormControl fullWidth variant="outlined" required error={Boolean(error)}>
                <InputLabel htmlFor="allergy-details">{t ? t('widgets.reservation.form.allergy.details') : 'Détails des allergies ou intolérances'}</InputLabel>
                <AllergyDetailsInput
                  id="allergy-details"
                  label={t ? t('widgets.reservation.form.allergy.details') : 'Détails des allergies ou intolérances'}
                  multiline
                  rows={3}
                  value={formData.allergyDetails || ''}
                  onChange={handleDetailsChange}
                  onClick={handleInputClick}
                  onFocus={handleInputClick}
                  onMouseDown={handleInputClick}
                  onMouseUp={handleInputClick}
                  onBlur={handleDetailsBlur}
                  // À câbler explicitement : MUI ne relie l'aide à l'input que dans
                  // TextField, pas avec OutlinedInput et FormHelperText nus.
                  aria-describedby={helperText ? 'allergy-details-helper' : undefined}
                  placeholder={t ? t('widgets.reservation.form.allergy.placeholder') : 'Précisez les allergies ou intolérances alimentaires...'}
                  startAdornment={
                    <InputAdornment position="start" sx={{alignSelf: 'flex-start', mt: 1.5}}>
                      <Iconify icon="mdi:gluten" fontSize="small" color="primary" aria-hidden="true" />
                    </InputAdornment>
                  }
                />
                {helperText && <FormHelperText id="allergy-details-helper">{helperText}</FormHelperText>}
              </FormControl>
            </Box>
          </Fade>
        )}
      </Box>
    </Box>
  );
});

AllergySection.propTypes = {
  formData: PropTypes.shape({
    hasAllergies: PropTypes.bool,
    allergyDetails: PropTypes.string,
  }).isRequired,
  updateFormData: PropTypes.func.isRequired,
  handleInputClick: PropTypes.func.isRequired,
  t: PropTypes.func, // Translation function
  error: PropTypes.bool,
  helperText: PropTypes.node,
  onDetailsBlur: PropTypes.func,
};
